// TradingView-style strategy settings surface — shared by the Strategy Builder
// and the Backtest window. Two tabs:
//   • Properties — engine/backtest config (initial capital, order size,
//     commission, slippage, pyramiding, recalc). A curated set of
//     strategy_config fields.
//   • Inputs — every tunable parameter the strategy exposes, auto-surfaced
//     from ALL blocks and grouped by block (indicator periods, SL/TP, entry
//     sizing, risk guards, …).
//
// Single source of truth: every edit persists into the actual TSL blocks via
// patchBlocks, so it's saved with the strategy/template and used identically
// by backtest, export, and deploy. No localStorage layer.

import React, { useState } from 'react';
import { Sliders, SlidersHorizontal, RotateCcw } from 'lucide-react';
import type { Block, BlockData } from '../../types';
import { BlockType } from '../../types';
import { useStrategy } from '../../contexts/StrategyContext';
import { getOriginalSizing, sizingChangedFromOriginal, SIZING_SNAPSHOT_FIELDS } from '../../services/originalSizing';
import {
  entryDeclaresSizing,
  effectiveSizingSource,
  applyDefaultOrderSizePatch,
} from './sizingAutoTarget';

interface Props {
  blocks: Block[];
  /** Optional starting tab. */
  defaultTab?: 'properties' | 'inputs';
}

type FieldType = 'number' | 'boolean' | 'text' | 'select';
interface FieldDef { key: string; label: string; type: FieldType; options?: string[]; optionLabels?: Record<string, string>; help?: string }

// ── Properties (engine config) — curated strategy_config fields ─────────────
const SIZING_METHODS = ['fixed', 'contracts', 'percent_equity', 'currency', 'risk_based', 'atr_risk', 'fixed_risk', 'pine_futures'];
// Default order size — three simple methods up front (Quantity / Cash /
// % of equity), with the advanced risk-based methods always listed below so
// switching between them is reversible (previously the dropdown only carried
// the 3 simple ones + whatever was currently set, which meant a method like
// "Integer contracts" disappeared the moment you switched away). Engine,
// converter, and exported Python all support every value in the list.
const DEFAULT_ORDER_SIZE_METHODS = [
  // Simple / TradingView-style.
  'fixed', 'currency', 'percent_equity',
  // Advanced / risk-based.
  'risk_based', 'atr_risk', 'fixed_risk', 'pine_futures', 'contracts',
];
const DEFAULT_ORDER_SIZE_LABELS: Record<string, string> = {
  fixed:          'Quantity (units / contracts)',
  currency:       'Cash value (USD / USDT …)',
  percent_equity: '% of equity',
  risk_based:     'Account risk % (at stop)',
  atr_risk:       'Account risk % (ATR-normalized)',
  fixed_risk:     'Fixed dollar risk per trade',
  pine_futures:   'Integer contracts (risk %, MDL-aware)',
  contracts:      'Fixed contracts (integer)',
};
const COMMISSION_TYPES = ['percentage', 'per_contract', 'per_share', 'fixed', 'tiered'];
const PROPERTY_FIELDS: FieldDef[] = [
  { key: 'initial_capital', label: 'Initial capital', type: 'number' },
  { key: 'currency', label: 'Base currency', type: 'text' },
  { key: 'default_qty_type', label: 'Default order size — method', type: 'select', options: DEFAULT_ORDER_SIZE_METHODS, optionLabels: DEFAULT_ORDER_SIZE_LABELS },
  { key: 'default_qty_value', label: 'Default order size — value', type: 'number' },
  // Opt-in dynamic notional cap. 0 = off (today's default — unlimited).
  // 1 = spot (notional <= equity, no leverage). Backtest + exported Python
  // both honor it via the same strategy_config field (parity-tested).
  { key: 'max_leverage', label: 'Max leverage (notional cap; 0 = off, 1 = spot)', type: 'number' },
  { key: 'pyramiding', label: 'Pyramiding (same-direction adds)', type: 'number' },
  { key: 'maxPositions', label: 'Max positions', type: 'number' },
  { key: 'commission_type', label: 'Commission type', type: 'select', options: COMMISSION_TYPES },
  { key: 'commission_value', label: 'Commission value', type: 'number' },
  { key: 'slippage', label: 'Slippage (bps / ticks)', type: 'number' },
  { key: 'max_drawdown', label: 'Max drawdown halt %', type: 'number' },
  { key: 'process_orders_on_close', label: 'Fill orders on bar close', type: 'boolean' },
  { key: 'calc_on_every_tick', label: 'Recalc on every tick', type: 'boolean' },
];
const PROPERTY_KEYS = new Set(PROPERTY_FIELDS.map(f => f.key));

// Keys never shown in Inputs. The surface auto-derives tunables from each
// block's primitive fields; this filters out everything that is NOT a user
// knob — identity/display, engine/market context, structural WIRING (how
// blocks link to one another), block-kind discriminators, and custom-block
// code bodies. Without this, the panel leaks "linked blocks" (expression
// operands) and raw `code` strings, which is confusing and un-TradingView-like.
const INPUT_DENYLIST = new Set<string>([
  // Identity / display / meta — never a tunable.
  'name', 'title', 'shortTitle', 'description', 'label', 'color', 'collapsed',
  'locked', 'version', 'author', 'tags', 'created', 'modified', 'overlay',
  'decimals', 'precision', 'id', 'blockId',
  // Engine / market context — set via the data selector or strategy meta, not
  // a per-strategy knob to expose here.
  'symbol', 'platform', 'assetClass', 'timeframe',
  // Prop-firm + sizing-defaults plumbing (Properties owns sizing/cost).
  'propFirm', 'propFirmPreset', 'useStrategyDefaults',
  // Structural WIRING — how blocks LINK to each other (expression operands,
  // logic gates, range bounds). These reference other blocks / ports, not
  // user values, so they must never appear as editable inputs.
  'inputs', 'outputs',
  'leftInputBlockId', 'rightInputBlockId', 'leftOutputPort', 'rightOutputPort',
  'leftSource', 'rightSource', 'leftPriceSource', 'rightPriceSource',
  'leftPriceDataBlockId', 'rightPriceDataBlockId',
  'rightLowInputBlockId', 'rightLowOutputPort', 'rightHighInputBlockId', 'rightHighOutputPort',
  'operator', 'persistence', 'invert', 'logic',
  // Block-kind discriminators + custom-block code body — structure, not knobs.
  'blockType', 'patternType', 'filterType', 'indicator', 'executionTier',
  // S4d (2026-06-02): 'outputTypes' removed from the structure-not-knobs
  // list — the field is gone from BlockData. 'outputType' (singular)
  // kept for any legacy strategy that carries it as a stray field; it
  // also has no engine consumer.
  'code', 'parameters', 'outputType',
]);

// Known enum option lists so string fields render as a dropdown (not free text).
const ENUM_OPTIONS: Record<string, string[]> = {
  default_qty_type: SIZING_METHODS,
  position_sizing_method: SIZING_METHODS,
  quantity_type: SIZING_METHODS,
  commission_type: COMMISSION_TYPES,
  equity_basis: ['current_equity', 'initial_capital'],
  direction: ['long', 'short'],
  entryType: ['market', 'limit', 'stop'],
  orderType: ['market', 'limit', 'bracket'],
  stopType: ['fixed_points', 'fixed_price', 'percentage', 'atr_based', 'trailing', 'trailing_limit', 'time_based', 'breakeven'],
  tpType: ['fixed_points', 'fixed_price', 'percentage', 'risk_reward'],
  operator: ['>', '<', '>=', '<=', '==', '!=', 'crossover', 'crossunder'],
  source: ['open', 'high', 'low', 'close', 'hl2', 'hlc3', 'ohlc4'],
  session: ['asian', 'european', 'american'],
};

// An expression operand's scalar value (leftValue / rightValue) is a genuine,
// user-tunable threshold ONLY when that side is a STATIC value — e.g. ADX > 25,
// RSI < 30, Volume > X. When the side is WIRED to another block/source
// (input / price / price_data / range_* / indicator / time / …) the scalar is
// an unused default (e.g. Right Value = 0) and must be hidden. Unset/empty
// source ⇒ treated as static (block default), so real thresholds aren't hidden.
const STATIC_OPERAND_SOURCES = new Set(['static', 'manual', 'value', 'const', 'constant', 'fixed', 'number', 'scalar']);
function operandSideIsWired(sourceVal: unknown): boolean {
  if (sourceVal === undefined || sourceVal === null || sourceVal === '') return false;
  return !STATIC_OPERAND_SOURCES.has(String(sourceVal).toLowerCase());
}

function humanize(key: string): string {
  return key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
    .trim();
}

const BLOCK_LABEL: Partial<Record<BlockType, string>> = {
  [BlockType.STRATEGY_CONFIG]: 'Strategy config',
  [BlockType.ENTRY]: 'Entry',
  [BlockType.STOP_LOSS]: 'Stop loss',
  [BlockType.TAKE_PROFIT]: 'Take profit',
  [BlockType.EXPRESSION]: 'Expression',
  [BlockType.MOVING_AVERAGE]: 'Moving average',
  [BlockType.MOMENTUM_OSCILLATOR]: 'Momentum',
  [BlockType.VOLATILITY]: 'Volatility',
};

const StrategySettings: React.FC<Props> = ({ blocks, defaultTab = 'inputs' }) => {
  const { patchBlocks, loadedAt } = useStrategy();
  const [tab, setTab] = useState<'properties' | 'inputs'>(defaultTab);

  const cfgBlock = blocks.find(b => b.type === BlockType.STRATEGY_CONFIG);
  // Any entry block carrying its own sizing fields ⇒ Properties → Default order
  // size is overridden for those entries (same semantic as Pine when
  // strategy.entry(qty=…) is passed). Drives the warning banner below.
  const entryOverridesSizing = blocks.some(
    b => b.type === BlockType.ENTRY && entryDeclaresSizing((b.data ?? {}) as Record<string, unknown>),
  );

  // ── "Restore original sizing" — revert sizing fields on config + entry blocks
  // to the snapshot captured when the strategy loaded (see originalSizing.ts).
  // This is the single recoverable entry point now that the standalone
  // Position-sizing panel is gone; it never feeds money math, only the editor.
  const sizingBlocks = blocks.filter(
    b => b.type === BlockType.STRATEGY_CONFIG || b.type === BlockType.ENTRY,
  );
  const anySizingChanged = sizingBlocks.some(b =>
    sizingChangedFromOriginal(loadedAt, b.id, (b.data ?? {}) as Record<string, unknown>),
  );
  const restoreAllSizing = () => {
    const next = blocks.map(b => {
      if (b.type !== BlockType.STRATEGY_CONFIG && b.type !== BlockType.ENTRY) return b;
      const baseline = getOriginalSizing(loadedAt, b.id);
      if (!baseline) return b;
      const data = { ...(b.data ?? {}) } as Record<string, unknown>;
      for (const k of SIZING_SNAPSHOT_FIELDS) {
        if (k in baseline) data[k] = baseline[k];
        else delete data[k];
      }
      return { ...b, data: data as BlockData };
    });
    patchBlocks(next);
  };

  const patchField = (blockId: string, key: string, value: unknown) => {
    // AUTO-TARGET WRITE for Properties → Default order size. Pine precedence
    // (entry-level > config) means writing this to config alone would be
    // silently ignored when entries declare their own sizing. Route through
    // applyDefaultOrderSizePatch which mirrors the change onto entry blocks
    // (the effective source) AND keeps strategy_config in sync. Pure; tested
    // in scripts/test-conversion.mjs §sizing src.
    if (blockId === cfgBlock?.id && (key === 'default_qty_type' || key === 'default_qty_value')) {
      patchBlocks(applyDefaultOrderSizePatch(blocks, key, value));
      return;
    }
    // Dot-path `parameters.<name>` — used by qty_calculator blocks whose
    // tunables live nested under data.parameters rather than as flat
    // block-data primitives. Write to the nested object without clobbering
    // sibling keys (e.g. the __canonical marker).
    if (key.startsWith('parameters.')) {
      const pKey = key.slice('parameters.'.length);
      patchBlocks(blocks.map(b => {
        if (b.id !== blockId) return b;
        const data = { ...(b.data ?? {}) } as Record<string, unknown>;
        const params = { ...((data.parameters ?? {}) as Record<string, unknown>) };
        params[pKey] = value;
        data.parameters = params;
        return { ...b, data: data as BlockData };
      }));
      return;
    }
    patchBlocks(blocks.map(b =>
      b.id === blockId ? { ...b, data: { ...(b.data ?? {}), [key]: value } as BlockData } : b
    ));
  };

  // Generic field control. Returns null for object/array values (not editable here).
  const renderField = (blockId: string, data: Record<string, unknown>, def: FieldDef) => {
    // Dot-path support so qty_calculator's nested `parameters.<name>` fields
    // can be rendered/edited alongside flat block-data primitives.
    const raw = def.key.startsWith('parameters.')
      ? ((data.parameters as Record<string, unknown> | undefined) ?? {})[def.key.slice('parameters.'.length)]
      : data[def.key];
    const onNum = (s: string) => patchField(blockId, def.key, s.trim() === '' ? undefined : Number(s));
    const common = 'w-full rounded border border-gray-300 px-2 py-1 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-blue-500';
    return (
      <label key={def.key} className="block text-xs">
        <span className="block text-gray-600 mb-0.5">{def.label}</span>
        {def.type === 'boolean' ? (
          <input type="checkbox" checked={!!raw} onChange={e => patchField(blockId, def.key, e.target.checked)} className="mt-0.5" />
        ) : def.type === 'number' ? (
          <input type="number" value={typeof raw === 'number' ? raw : ''} onChange={e => onNum(e.target.value)} className={`${common} font-mono`} />
        ) : def.type === 'select' ? (
          <select value={typeof raw === 'string' ? raw : ''} onChange={e => patchField(blockId, def.key, e.target.value)} className={common}>
            {/* Empty / unset → placeholder. A non-empty value that isn't one of
                the offered options (e.g. an advanced sizing method on a config
                whose dropdown only offers the 3 TradingView defaults) is shown
                as its own option so it's visible and never silently dropped. */}
            {(typeof raw !== 'string' || raw === '') && <option value="">—</option>}
            {typeof raw === 'string' && raw !== '' && !def.options?.includes(raw) && (
              <option value={raw}>{def.optionLabels?.[raw] ?? humanize(raw)}</option>
            )}
            {(def.options ?? []).map(o => <option key={o} value={o}>{def.optionLabels?.[o] ?? o}</option>)}
          </select>
        ) : (
          <input type="text" value={typeof raw === 'string' ? raw : ''} onChange={e => patchField(blockId, def.key, e.target.value)} className={common} />
        )}
      </label>
    );
  };

  // Auto-derive a block's tunable fields (primitives, not denied, not Properties).
  const inputFieldsFor = (block: Block): FieldDef[] => {
    const data = (block.data ?? {}) as Record<string, unknown>;
    const isConfig = block.type === BlockType.STRATEGY_CONFIG;
    const defs: FieldDef[] = [];
    // Per-trade custom blocks (qty_calculator / sl_calculator / tp_calculator)
    // carry their tunables under data.parameters rather than as flat block-data
    // primitives. Expand those for Inputs so they're editable like every other
    // tunable. patchField targets `parameters.<key>` via dot path.
    // Slice B.1 — risk_rule joins the per-trade-custom Inputs surface.
    // Rule parameters (maxTradesPerDay, enabled, …) are editable like
    // any other per-block tunable.
    const perTradeCustom = data.blockType === 'qty_calculator'
                        || data.blockType === 'sl_calculator'
                        || data.blockType === 'tp_calculator'
                        || data.blockType === 'risk_rule';
    if (perTradeCustom && data.parameters && typeof data.parameters === 'object') {
      const params = data.parameters as Record<string, unknown>;
      for (const [pKey, pVal] of Object.entries(params)) {
        // Skip internal markers (e.g. __canonical: 'ftmo_calcqty_v1').
        if (pKey.startsWith('__')) continue;
        const t = typeof pVal;
        if (t === 'number') defs.push({ key: `parameters.${pKey}`, label: humanize(pKey), type: 'number' });
        else if (t === 'boolean') defs.push({ key: `parameters.${pKey}`, label: humanize(pKey), type: 'boolean' });
        else if (t === 'string') defs.push({ key: `parameters.${pKey}`, label: humanize(pKey), type: 'text' });
      }
      return defs.sort((a, b) => a.label.localeCompare(b.label));
    }
    for (const [key, val] of Object.entries(data)) {
      if (INPUT_DENYLIST.has(key)) continue;
      // Any block-reference id (…BlockId — e.g. linkedStopLossBlockId,
      // targetEntryBlockId, *InputBlockId, *PriceDataBlockId) is structural
      // wiring, never a user knob. Pattern-match so new link fields can't leak.
      if (key.toLowerCase().endsWith('blockid')) continue;
      // Expression operand scalars: hide leftValue/rightValue when that side is
      // wired to another block/source (the scalar is then an unused default);
      // keep them when the side is a static threshold (ADX > 25, RSI < 30, …).
      if (key === 'leftValue' && operandSideIsWired(data.leftSource)) continue;
      if (key === 'rightValue' && operandSideIsWired(data.rightSource)) continue;
      if (isConfig && PROPERTY_KEYS.has(key)) continue; // lives in Properties
      const t = typeof val;
      if (t === 'number') defs.push({ key, label: humanize(key), type: 'number' });
      else if (t === 'boolean') defs.push({ key, label: humanize(key), type: 'boolean' });
      else if (t === 'string') {
        const options = ENUM_OPTIONS[key];
        defs.push({ key, label: humanize(key), type: options ? 'select' : 'text', options });
      }
      // objects / arrays / null → skip
    }
    return defs.sort((a, b) => a.label.localeCompare(b.label));
  };

  // Block render order: config, entries, SL, TP, then the rest.
  const ORDER: BlockType[] = [BlockType.STRATEGY_CONFIG, BlockType.ENTRY, BlockType.STOP_LOSS, BlockType.TAKE_PROFIT];
  const orderedBlocks = [...blocks].sort((a, b) => {
    const ia = ORDER.indexOf(a.type); const ib = ORDER.indexOf(b.type);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  });

  return (
    <div className="rounded-md border border-gray-200 bg-white">
      <div className="flex items-center gap-1 border-b border-gray-200 px-2 pt-2">
        <button
          type="button"
          onClick={() => setTab('inputs')}
          className={`flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-t ${tab === 'inputs' ? 'text-blue-700 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
        >
          <Sliders className="h-3.5 w-3.5" /> Inputs
        </button>
        <button
          type="button"
          onClick={() => setTab('properties')}
          className={`flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-t ${tab === 'properties' ? 'text-blue-700 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
        >
          <SlidersHorizontal className="h-3.5 w-3.5" /> Properties
        </button>
        {anySizingChanged && (
          <button
            type="button"
            onClick={restoreAllSizing}
            title="Revert all order-size fields to the values this strategy was loaded with"
            className="ml-auto mb-1 flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-amber-700 hover:bg-amber-50"
          >
            <RotateCcw className="h-3.5 w-3.5" /> Restore original sizing
          </button>
        )}
      </div>

      <div className="p-3">
        <p className="text-[11px] text-gray-500 mb-3">
          Changes save into the strategy and apply everywhere — backtest, export, and deploy — and persist when you Save the strategy.
        </p>

        {tab === 'properties' ? (
          !cfgBlock ? (
            <div className="text-xs text-gray-400">No strategy_config block in this strategy.</div>
          ) : (
            <>
              {entryOverridesSizing && (
                <div className="mb-2 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-2 text-[11px] text-amber-900">
                  <span className="font-medium">This strategy has entry-level qty sizing.</span>{' '}
                  Default order size edits are mirrored onto the entry blocks so the change actually takes effect; the engine reads sizing from the entry block first. You can also fine-tune per-entry sizing in the Inputs tab.
                </div>
              )}
              {/* Effective sizing source — surfaced so the user always knows
                  which TSL layer drives qty (entry blocks vs strategy_config).
                  Coupled with the auto-target patch above, the Properties
                  choice is always effective, but the LABEL still tells the
                  truth about which block holds the live value. */}
              <p className="mb-3 text-[11px] text-gray-600">
                <span className="font-medium">Effective sizing source:</span>{' '}
                {(() => {
                  const src = effectiveSizingSource(blocks);
                  if (src === 'qty_calculator') {
                    // Surface the actual calculator block's name so the user can
                    // jump to it on the canvas / find it in Inputs.
                    const entry = blocks.find(b =>
                      b.type === BlockType.ENTRY
                      && (b.data as Record<string, unknown>)?.qtyCalculatorBlockId !== undefined,
                    );
                    const calcId = entry && (entry.data as Record<string, unknown>).qtyCalculatorBlockId as string | undefined;
                    const calc = calcId ? blocks.find(b => b.id === calcId) : undefined;
                    const calcName = calc ? String((calc.data as Record<string, unknown>).name ?? 'Qty calculator') : 'Qty calculator';
                    return (
                      <>
                        Qty calculator block (<code>{calcName}</code>) — Default order size below is informational only.
                      </>
                    );
                  }
                  return src === 'entry' ? 'Entry blocks' : 'Strategy defaults';
                })()}
              </p>
              <div className="grid grid-cols-2 gap-x-3 gap-y-2.5">
                {PROPERTY_FIELDS.map(def => renderField(cfgBlock.id, (cfgBlock.data ?? {}) as Record<string, unknown>, def))}
              </div>
            </>
          )
        ) : (
          <div className="space-y-4">
            {orderedBlocks.map(block => {
              const defs = inputFieldsFor(block);
              if (defs.length === 0) return null;
              const data = (block.data ?? {}) as Record<string, unknown>;
              const groupName = String(data.name ?? '') || BLOCK_LABEL[block.type] || humanize(String(block.type));
              return (
                <div key={block.id}>
                  <h4 className="text-xs font-semibold text-gray-700 mb-1.5">{groupName}</h4>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-2.5">
                    {defs.map(def => renderField(block.id, data, def))}
                  </div>
                </div>
              );
            })}
            {orderedBlocks.every(b => inputFieldsFor(b).length === 0) && (
              <div className="text-xs text-gray-400">This strategy exposes no tunable inputs.</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default StrategySettings;
